import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Tab, Nav } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { getFarmData, updateFarm } from '../../services/Farm/farmService';
import ErrorModal from '../Modals/ErrorModal';
import SuccessModal from '../Modals/SuccessModal';
import DocumentInput from '../Utils/DocumentInput';
import PhoneInput from '../Utils/PhoneInput';
import ZipCodeInput from '../Utils/ZipCodeInput';
import InputField from '../Utils/InputField';
import UFSelect from '../Utils/UFSelect';
import PersonInput from '../Utils/PersonInput';

const EditFarmModal = ({ show, handleClose, farm, refreshFarms }) => {
  const { register, handleSubmit, setValue, control, formState: { errors }, reset, watch } = useForm();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeKey, setActiveKey] = useState('general');
  const [modalKey, setModalKey] = useState(0);

  useEffect(() => {
    const fetchFarmData = async () => {
      try {
        if (farm?.id) {
          const data = await getFarmData(farm.id);
          setValue('oldId', data?.oldId);
          setValue('name', data.name);
          setValue('document', data.document);
          setValue('registration', data.registration);
          setValue('businessPhone', data.businessPhone);
          setValue('cellPhone', data.cellPhone);
          setValue('mail', data.mail);
          setValue('address', data.address);
          setValue('district', data.district);
          setValue('city', data.city);
          setValue('state', data.state);
          setValue('zipCode', data.zipCode);
          setValue('observations', data.observations);
          setValue('personId', { id: data.person?.id, name: data.owner });
          setValue('tenant', data.tenant);
        }
      } catch (error) {
        setErrorMessage(error.message || 'Erro ao buscar dados da fazenda.');
        setShowErrorModal(true);
      }
    };

    if (show && farm) {
      fetchFarmData();
    } 
    else if (!show) {
      reset();
      setModalKey((prevKey) => prevKey + 1);
    }
  }, [show, farm, setValue, reset]);

  const onSubmit = async (data) => {
    const payload = {
      oldId: data.oldId,
      name: data.name,
      document: data.document,
      registration: data.registration,
      businessPhone: data.businessPhone,
      cellPhone: data.cellPhone,
      mail: data.mail,
      address: data.address,
      district: data.district,
      city: data.city,
      state: data.state,
      zipCode: data.zipCode,
      observations: data.observations,
      personId: data.personId?.id,
      owner: data.personId?.name || '',
      tenant: data.tenant
    };

    try {
      await updateFarm(farm.id, payload);
      setSuccessMessage('Fazenda atualizada com sucesso!');
      setShowSuccessModal(true);
      reset();
    } catch (error) {
      setErrorMessage(error.message || 'Erro ao atualizar fazenda.');
      setShowErrorModal(true);
    }
  };

  const handleCloseErrorModal = () => setShowErrorModal(false);
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    refreshFarms();
    handleClose();
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} centered backdrop="static" keyboard={false} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Editar Fazenda</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Tab.Container activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
              <Nav variant="tabs" className="mb-3">
                <Nav.Item>
                  <Nav.Link eventKey="general">Informações Gerais</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="address">Endereço</Nav.Link>
                </Nav.Item>
              </Nav>
              <Tab.Content>
                <Tab.Pane eventKey="general">
                  <Row>
                    <Col md={8}>
                      <InputField
                        label="Nome da Fazenda"
                        name="name"
                        placeholder="Digite o nome"
                        register={register}
                        errors={errors}
                        rules={{ required: 'Nome é obrigatório' }}
                      />
                    </Col>
                    <Col md={4}>
                      <DocumentInput
                        key={modalKey}
                        label="CPF/CNPJ"
                        name="document"
                        register={register}
                        errors={errors}
                        value={watch('document')}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <InputField
                        label="Inscr. Estadual"
                        name="registration"
                        placeholder="Digite a Inscrição Estadual"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                    <Col md={4}>
                      <PhoneInput
                        key={modalKey}
                        label="Telefone Comercial"
                        name="businessPhone"
                        register={register}
                        errors={errors}
                        value={watch('businessPhone')}
                      />
                    </Col>
                    <Col md={4}>
                      <PhoneInput
                        key={modalKey}
                        label="Telefone Celular"
                        name="cellPhone"
                        register={register}
                        errors={errors}
                        value={watch('cellPhone')}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <InputField
                        label="Email"
                        name="mail"
                        placeholder="Digite o email"
                        register={register}
                        errors={errors}
                        rules={{
                          pattern: {
                            value: /^\S+@\S+$/i,
                            message: 'Endereço de email inválido',
                          }
                        }}
                      />
                    </Col>
                    <Col md={6}>
                      <InputField
                        label="Observações"
                        name="observations"
                        placeholder="Digite observações"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Controller
                        name="personId"
                        control={control}
                        render={({ field, fieldState }) => (
                          <PersonInput
                            value={field.value}
                            onChange={(selectedPerson) => setValue('personId', selectedPerson)}
                            label="Proprietário"
                            isInvalid={!!fieldState.error}
                            createdNew={true}
                            type={['isCustomer', 'isProducer', 'isSupplier']}
                          />
                        )}
                      />
                    </Col>
                    <Col md={6}>
                      <InputField
                        label="Arrendatário"
                        name="tenant"
                        placeholder="Informe o tipo do arrendatário"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                  </Row>
                </Tab.Pane>
                <Tab.Pane eventKey="address">
                  <Row>
                    <Col md={6}>
                      <InputField
                        label="Endereço"
                        name="address"
                        placeholder="Digite o endereço"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                    <Col md={6}>
                      <InputField
                        label="Bairro"
                        name="district"
                        placeholder="Digite o bairro"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={5}>
                      <InputField
                        label="Cidade"
                        name="city"
                        placeholder="Digite a cidade"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                    <Col md={4}>
                      <UFSelect
                        label="Estado"
                        name="state"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                    <Col md={3}>
                      <ZipCodeInput
                        label="CEP"
                        name="zipCode"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                  </Row>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
            <Button variant="primary" className="button-custom w-100 mt-3" type="submit">
              Atualizar
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <ErrorModal
        show={showErrorModal}
        handleClose={handleCloseErrorModal}
        errorMessage={errorMessage}
      />

      <SuccessModal
        show={showSuccessModal}
        handleClose={handleCloseSuccessModal}
        successMessage={successMessage}
      />
    </>
  );
};

export default EditFarmModal;